import { classifySearch } from '../GlobalMapSearch.ai';
import type { ThunkDispatch } from '../GlobalMapSearch.poi';
import type {
  AdrDataByTopic,
  AlertsById,
  AllTextMessages,
  CallQueue,
  MapCenter,
  ReifiedLayer,
  SearchDataset,
  SearchResultItem,
  SpatialLayer,
  TranscriptionsByCallId,
} from '../GlobalMapSearch.types';
import { runAlertsSearch } from './alerts/alertsSearch';
import { runCallsSearch } from './calls/callsSearch';
import { runAxonSearch } from './dynamic/axonSearch';
import { runDynamicLayerSearch } from './dynamic/dynamicLayerSearch';
import { getSearchExecutionPlan } from './executionPlan';
import { runGeocodeSearch } from './geocode/geocodeSearch';
import { buildGisLayerSearchUnits } from './gis/gisLayerSearch';
import { runMessagesSearch } from './messages/messagesSearch';
import { buildRuntimeFlags, type SourceStatus } from './runtimeContext';
import { runStaticLayerSearch } from './static/staticLayerSearch';
import { runTranscriptsSearch } from './transcripts/transcriptsSearch';

export interface RunGlobalMapSearchArgs {
  text: string;
  layers: ReifiedLayer[];
  axonDs: SearchDataset[] | undefined;
  staticLayers: SpatialLayer[];
  dynamicLayers: SpatialLayer[];
  callQueue: CallQueue | undefined;
  transcriptions: TranscriptionsByCallId | undefined;
  messages: AllTextMessages | undefined;
  alerts: AlertsById | undefined;
  removedAlerts: string[] | undefined;
  alertAdrData: AdrDataByTopic | undefined;
  serviceUrl?: string;
  token?: string | null;
  signal: AbortSignal;
  mapCenterRef: React.MutableRefObject<MapCenter | undefined>;
  jurisdictionBoundaryRef: React.MutableRefObject<unknown>;
  isFocusedRef: React.MutableRefObject<boolean>;
  thunkDispatch: ThunkDispatch;
  setResults: (results: SearchResultItem[]) => void;
  setIsSearching: (isSearching: boolean) => void;
  setSearchSources: (sources: SourceStatus[]) => void;
  setIsOpen: (open: boolean) => void;
  setCursor: (cursor: number) => void;
}

/**
 * Drive a single search: classify the query, decide which executors to run,
 * and stream partial results into state as each source completes.
 *
 * Reads like: classify → plan → fan out (static, dynamic+axon, GIS layers,
 * geocode) → collect.
 */
export async function runGlobalMapSearch({
  text,
  layers,
  axonDs,
  staticLayers,
  dynamicLayers,
  callQueue,
  transcriptions,
  messages,
  alerts,
  removedAlerts,
  alertAdrData,
  serviceUrl,
  token,
  signal,
  mapCenterRef,
  jurisdictionBoundaryRef,
  isFocusedRef,
  thunkDispatch,
  setResults,
  setIsSearching,
  setSearchSources,
  setIsOpen,
  setCursor,
}: RunGlobalMapSearchArgs): Promise<void> {
  const collected: SearchResultItem[] = [];
  const sources: SourceStatus[] = [];

  setSearchSources([{ name: 'Classifying query', status: 'pending' }]);
  const query = await classifySearch(text, signal);

  if (signal.aborted) return;

  const primary = query.labels[0];
  const mapCenter = mapCenterRef.current;
  const flags = buildRuntimeFlags({
    layers,
    mapCenter,
    serviceUrl,
    staticLayers,
    dynamicLayers,
    callQueue,
    transcriptions,
    messages,
    alerts,
  });
  const plan = getSearchExecutionPlan(primary, flags);

  const flush = () => {
    if (signal.aborted) return;
    setResults([...collected]);
    if (isFocusedRef.current && collected.length > 0) setIsOpen(true);
  };

  const updateSources = () => {
    setSearchSources(sources.map(s => ({ ...s })));
  };

  const markDone = (idx: number) => {
    sources[idx].status = 'done';
    flush();
    updateSources();
    if (sources.every(s => s.status !== 'pending')) {
      setIsSearching(false);
      setCursor(-1);
    }
  };

  // --- Synchronous spatial sources (static + dynamic + axon) ---
  if (plan.runStaticLayers) {
    runStaticLayerSearch({ text, layers: staticLayers, collected, flush });
  }

  if (plan.runDynamicLayers) {
    // Axon keeps its specialized officer/badge fuzzy match; everything else
    // dynamic goes through the generic spatial-attribute path.
    runAxonSearch({ text, axonDs, collected, flush });
    runDynamicLayerSearch({
      text,
      layers: dynamicLayers,
      collected,
      flush,
    });
  }

  // --- Active emergency calls (filter by phone digits) ---
  if (plan.runCalls) {
    runCallsSearch({
      text,
      callQueue,
      normalizedPhone: query.normalizedPhone,
      collected,
      flush,
    });
  }

  // --- Active alerts (id, type, status, location + ADR leaves) ---
  if (plan.runAlerts) {
    runAlertsSearch({
      text,
      alerts,
      removedAlerts,
      adrData: alertAdrData,
      collected,
      flush,
    });
  }

  // --- Live transcriptions (substring against displayed token text) ---
  if (plan.runTranscripts) {
    runTranscriptsSearch({ text, transcriptions, collected, flush });
  }

  // --- TalkJS chat messages tied to active calls (substring on body) ---
  if (plan.runMessages) {
    runMessagesSearch({ text, messages, collected, flush });
  }

  // --- Build all async search tasks ---
  const tasks: Promise<void>[] = [];

  if (plan.runGisLayers) {
    const gisUnits = buildGisLayerSearchUnits({
      text,
      primary,
      normalizedPhone: query.normalizedPhone,
      layers,
      signal,
      jurisdictionBoundaryRef,
      mapCenterRef,
      collected,
      flush,
    });
    for (const unit of gisUnits) {
      const idx = sources.length;
      sources.push({ name: unit.title, status: 'pending' });
      tasks.push(unit.run().finally(() => markDone(idx)));
    }
  }

  // --- Address / POI task ---
  if (
    plan.runGeocode &&
    mapCenter?.latitude != null &&
    mapCenter?.longitude != null
  ) {
    const idx = sources.length;
    sources.push({ name: 'Addresses & POI', status: 'pending' });
    tasks.push(
      runGeocodeSearch({
        text,
        lat: mapCenter.latitude,
        lng: mapCenter.longitude,
        serviceUrl,
        token,
        signal,
        collected,
        flush,
        thunkDispatch,
      }).finally(() => markDone(idx)),
    );
  }

  updateSources();

  if (tasks.length > 0) {
    await Promise.allSettled(tasks);
  } else {
    setIsSearching(false);
    setSearchSources([]);
  }

  if (!signal.aborted && collected.length > 0) {
    setResults([...collected]);
    // Mirror the gating in `flush` / `triggerSearch`: don't reopen the
    // dropdown if the user has clicked away while async tasks were in flight.
    if (isFocusedRef.current) setIsOpen(true);
    setCursor(-1);
  }
}
